import React from 'react';
import Icon from '../../../components/AppIcon';

const ProductionAreaTile = ({ area, onClick }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'bg-success/10 border-success text-success';
      case 'warning':
        return 'bg-warning/10 border-warning text-warning';
      case 'critical':
        return 'bg-error/10 border-error text-error';
      case 'offline':
        return 'bg-muted border-border text-muted-foreground';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return 'CheckCircle2';
      case 'warning':
        return 'AlertTriangle';
      case 'critical':
        return 'AlertOctagon';
      case 'offline':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'operational':
        return 'Operational';
      case 'warning':
        return 'Warning';
      case 'critical':
        return 'Critical';
      case 'offline':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      onClick={() => onClick(area)}
      className="bg-card rounded-xl border-2 border-border p-4 md:p-5 lg:p-6 hover:border-primary transition-all duration-250 cursor-pointer hover:shadow-lg w-full min-w-0"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-semibold text-foreground truncate">
            {area?.name}
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">
            {area?.line}
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 ${getStatusColor(area?.status)} flex-shrink-0`}>
          <Icon
            name={getStatusIcon(area?.status)}
            size={16}
            color={area?.status === 'operational' ? 'var(--color-success)' : area?.status === 'warning' ? 'var(--color-warning)' : area?.status === 'critical' ? 'var(--color-error)' : 'var(--color-muted-foreground)'}
          />
          <span className="text-xs md:text-sm font-medium whitespace-nowrap">
            {getStatusLabel(area?.status)}
          </span>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs md:text-sm text-muted-foreground">Task Progress</span>
            <span className="text-xs md:text-sm font-semibold text-foreground data-text">
              {area?.completionPercentage}%
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${area?.completionPercentage}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
              <span className="caption text-muted-foreground">Throughput</span>
            </div>
            <p className="text-sm md:text-base font-semibold text-foreground data-text">
              {area?.throughput}
            </p>
          </div>

          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Users" size={16} color="var(--color-primary)" />
              <span className="caption text-muted-foreground">Workers</span>
            </div>
            <p className="text-sm md:text-base font-semibold text-foreground data-text">
              {area?.workerCount}
            </p>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="caption text-muted-foreground">Equipment Status</span>
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${area?.equipmentConnected ? 'bg-success' : 'bg-error'}`} />
              <span className="caption text-muted-foreground">
                {area?.equipmentConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {area?.equipment?.map((eq, index) => (
              <div
                key={index}
                className="flex items-center gap-1.5 px-2 py-1 bg-background rounded-md"
              >
                <div className={`w-1.5 h-1.5 rounded-full ${eq?.operational ? 'bg-success' : 'bg-error'}`} />
                <span className="caption text-foreground">{eq?.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <span className="caption text-muted-foreground">Last Update</span>
          <span className="caption text-foreground data-text">
            {formatTimestamp(area?.lastUpdate)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductionAreaTile;